<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePipelinesStore } from 'dashboard/stores/pipelines';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ConfirmPipelineDeleteDialog from 'dashboard/components-next/Deals/ConfirmPipelineDeleteDialog.vue';

const { t } = useI18n();
const pipelinesStore = usePipelinesStore();

const deleteDialogRef = ref(null);
const pipelineToDelete = ref(null);

const pipelines = computed(() =>
  [...pipelinesStore.getPipelinesList].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  )
);
const isFetching = computed(() => pipelinesStore.uiFlags.fetchingList);
const isCreatingPipeline = computed(() => pipelinesStore.uiFlags.creatingItem);

const newPipelineName = ref('');
const editingPipeline = ref(null);
const editingPipelineName = ref('');
const newStageName = ref({});
const editingStage = ref(null);
const editingStageId = ref(null);
const editingStageName = ref('');
const editingStageColor = ref('#6366f1');
const savingStage = ref(false);
const draggingStageId = ref(null);
const dragOverStageId = ref(null);
const draggingPipelineId = ref(null);
const dragOverPipelineId = ref(null);

const PRESET_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#64748b',
];

const isTerminalStage = stage =>
  stage.stageType === 'won' || stage.stageType === 'lost';

const createPipeline = async () => {
  // Guarda contra duplo disparo (Enter no input + clique no botão).
  if (isCreatingPipeline.value) return;
  const name = newPipelineName.value.trim();
  if (!name) return;
  try {
    await pipelinesStore.create({ name });
    newPipelineName.value = '';
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_CREATED'));
  } catch {
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_CREATE_ERROR'));
  }
};

const startEditPipeline = pipeline => {
  editingPipeline.value = pipeline.id;
  editingPipelineName.value = pipeline.name;
};

const saveEditPipeline = async pipeline => {
  const name = editingPipelineName.value.trim();
  if (!name || name === pipeline.name) {
    editingPipeline.value = null;
    return;
  }
  try {
    await pipelinesStore.update({ id: pipeline.id, name });
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_UPDATED'));
  } catch {
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_UPDATE_ERROR'));
  } finally {
    editingPipeline.value = null;
  }
};

const runDeletePipeline = async (pipeline, params = {}) => {
  try {
    await pipelinesStore.delete(pipeline.id, params);
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_DELETED'));
  } catch {
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_DELETE_ERROR'));
  }
};

const deletePipeline = async pipeline => {
  // Com negociações: abrir diálogo que obriga a decidir o destino delas.
  if ((pipeline.dealsCount ?? 0) > 0) {
    pipelineToDelete.value = pipeline;
    deleteDialogRef.value?.open();
    return;
  }
  // Sem negociações: confirmação simples.
  if (
    !window.confirm(
      t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_DELETE_CONFIRM', {
        name: pipeline.name,
      })
    )
  )
    return;
  await runDeletePipeline(pipeline);
};

const handleMoveDeals = async ({ targetStageId }) => {
  deleteDialogRef.value?.close();
  await runDeletePipeline(pipelineToDelete.value, {
    action_on_deals: 'move',
    target_stage_id: targetStageId,
  });
};

const handleDeleteDeals = async () => {
  deleteDialogRef.value?.close();
  await runDeletePipeline(pipelineToDelete.value, {
    action_on_deals: 'delete',
  });
};

const createStage = async pipeline => {
  if (savingStage.value) return;
  const name = (newStageName.value[pipeline.id] || '').trim();
  if (!name) return;
  try {
    savingStage.value = true;
    const regularPositions = (pipeline.stages || [])
      .filter(s => !isTerminalStage(s))
      .map(s => s.position);
    const position =
      (regularPositions.length ? Math.max(...regularPositions) : -1) + 1;
    await pipelinesStore.createStage(pipeline.id, { name, position });
    newStageName.value = { ...newStageName.value, [pipeline.id]: '' };
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.STAGE_CREATED'));
  } catch {
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.STAGE_CREATE_ERROR'));
  } finally {
    savingStage.value = false;
  }
};

const startEditStage = stage => {
  editingStage.value = stage.id;
  editingStageId.value = stage.id;
  editingStageName.value = stage.name;
  editingStageColor.value = stage.color || '#6366f1';
};

const saveEditStage = async (pipeline, stage) => {
  const name = editingStageName.value.trim();
  if (!name) {
    editingStage.value = null;
    return;
  }
  try {
    await pipelinesStore.updateStage(pipeline.id, stage.id, {
      name,
      color: editingStageColor.value,
    });
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.STAGE_UPDATED'));
  } catch {
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.STAGE_UPDATE_ERROR'));
  } finally {
    editingStage.value = null;
  }
};

const deleteStage = async (pipeline, stage) => {
  if (
    !window.confirm(
      t('PIPELINE_SETTINGS.MESSAGES.STAGE_DELETE_CONFIRM', { name: stage.name })
    )
  )
    return;
  try {
    await pipelinesStore.deleteStage(pipeline.id, stage.id);
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.STAGE_DELETED'));
  } catch {
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.STAGE_DELETE_ERROR'));
  }
};

const sortedStages = pipeline =>
  [...(pipeline.stages || [])].sort((a, b) => a.position - b.position);

const draggableStages = pipeline =>
  sortedStages(pipeline).filter(s => !isTerminalStage(s));

const terminalStages = pipeline =>
  sortedStages(pipeline).filter(s => isTerminalStage(s));

const onStageDragStart = (event, stage) => {
  draggingStageId.value = stage.id;
  event.dataTransfer.effectAllowed = 'move';
};

const onStageDragOver = (event, stage) => {
  if (isTerminalStage(stage)) return;
  event.preventDefault();
  dragOverStageId.value = stage.id;
};

const onStageDrop = async (event, pipeline, targetStage) => {
  event.preventDefault();
  if (!draggingStageId.value || draggingStageId.value === targetStage.id) {
    draggingStageId.value = null;
    dragOverStageId.value = null;
    return;
  }
  const stages = draggableStages(pipeline);
  const fromIdx = stages.findIndex(s => s.id === draggingStageId.value);
  const toIdx = stages.findIndex(s => s.id === targetStage.id);
  if (fromIdx === -1 || toIdx === -1) return;

  const reordered = [...stages];
  const [moved] = reordered.splice(fromIdx, 1);
  reordered.splice(toIdx, 0, moved);

  draggingStageId.value = null;
  dragOverStageId.value = null;

  await Promise.all(
    reordered.map((s, idx) => {
      if (s.position !== idx) {
        return pipelinesStore.updateStage(pipeline.id, s.id, { position: idx });
      }
      return Promise.resolve();
    })
  );
};

const onStageDragEnd = () => {
  draggingStageId.value = null;
  dragOverStageId.value = null;
};

const onPipelineDragStart = (event, pipeline) => {
  draggingPipelineId.value = pipeline.id;
  event.dataTransfer.effectAllowed = 'move';
};

const onPipelineDragOver = (event, pipeline) => {
  event.preventDefault();
  dragOverPipelineId.value = pipeline.id;
};

const onPipelineDragEnd = () => {
  draggingPipelineId.value = null;
  dragOverPipelineId.value = null;
};

const onPipelineDrop = async (event, targetPipeline) => {
  event.preventDefault();
  const draggedId = draggingPipelineId.value;
  draggingPipelineId.value = null;
  dragOverPipelineId.value = null;
  if (!draggedId || draggedId === targetPipeline.id) return;

  const current = pipelines.value;
  const fromIdx = current.findIndex(p => p.id === draggedId);
  const toIdx = current.findIndex(p => p.id === targetPipeline.id);
  if (fromIdx === -1 || toIdx === -1) return;

  const snapshot = current.map(p => ({ id: p.id, position: p.position }));
  const reordered = [...current];
  const [moved] = reordered.splice(fromIdx, 1);
  reordered.splice(toIdx, 0, moved);

  try {
    await Promise.all(
      reordered.map((p, idx) => {
        if (p.position !== idx) {
          return pipelinesStore.update({ id: p.id, position: idx });
        }
        return Promise.resolve();
      })
    );
  } catch {
    await Promise.all(
      snapshot.map(p =>
        pipelinesStore.update({ id: p.id, position: p.position })
      )
    );
    useAlert(t('PIPELINE_SETTINGS.MESSAGES.PIPELINE_UPDATE_ERROR'));
  }
};

onMounted(() => pipelinesStore.get());
</script>

<template>
  <SettingsLayout>
    <BaseSettingsHeader
      :title="t('PIPELINE_SETTINGS.TITLE')"
      :description="t('PIPELINE_SETTINGS.DESCRIPTION')"
    />

    <div class="flex flex-col gap-6 mt-6 max-w-3xl">
      <div class="flex gap-2">
        <Input
          v-model="newPipelineName"
          :placeholder="t('PIPELINE_SETTINGS.NEW_PIPELINE_PLACEHOLDER')"
          custom-input-class="h-9"
          class="flex-1"
          @keydown.enter="createPipeline"
        />
        <Button
          :label="t('PIPELINE_SETTINGS.CREATE_PIPELINE')"
          color="blue"
          :is-loading="isCreatingPipeline"
          :disabled="!newPipelineName.trim() || isCreatingPipeline"
          @click="createPipeline"
        />
      </div>

      <div v-if="isFetching" class="text-sm text-n-slate-10 py-4">
        {{ t('PIPELINE_SETTINGS.LOADING') }}
      </div>

      <div
        v-else-if="pipelines.length === 0"
        class="text-sm text-n-slate-10 py-4"
      >
        {{ t('PIPELINE_SETTINGS.EMPTY') }}
      </div>

      <div
        v-for="pipeline in pipelines"
        :key="pipeline.id"
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-alpha-1 transition-opacity"
        :class="[
          draggingPipelineId === pipeline.id ? 'opacity-40' : '',
          dragOverPipelineId === pipeline.id &&
          draggingPipelineId !== pipeline.id
            ? 'outline outline-2 outline-n-brand'
            : '',
        ]"
        :draggable="editingPipeline !== pipeline.id"
        @dragstart="onPipelineDragStart($event, pipeline)"
        @dragover="onPipelineDragOver($event, pipeline)"
        @drop="onPipelineDrop($event, pipeline)"
        @dragend="onPipelineDragEnd"
      >
        <div class="flex items-center justify-between gap-2">
          <div v-if="editingPipeline === pipeline.id" class="flex gap-2 flex-1">
            <Input
              v-model="editingPipelineName"
              custom-input-class="h-8"
              class="flex-1"
              autofocus
              @keydown.enter="saveEditPipeline(pipeline)"
              @keydown.escape="editingPipeline = null"
            />
            <Button
              :label="t('PIPELINE_SETTINGS.SAVE')"
              color="blue"
              size="sm"
              @click="saveEditPipeline(pipeline)"
            />
            <Button
              :label="t('PIPELINE_SETTINGS.CANCEL')"
              variant="ghost"
              size="sm"
              @click="editingPipeline = null"
            />
          </div>
          <div v-else class="flex items-center gap-2 min-w-0">
            <span
              class="i-lucide-grip-vertical text-n-slate-7 cursor-grab active:cursor-grabbing flex-shrink-0"
            />
            <span class="text-sm font-semibold text-n-slate-12 truncate">
              {{ pipeline.name }}
            </span>
          </div>
          <div v-if="editingPipeline !== pipeline.id" class="flex gap-1">
            <Button
              icon="i-lucide-pencil"
              variant="ghost"
              size="sm"
              color="slate"
              @click="startEditPipeline(pipeline)"
            />
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              size="sm"
              color="ruby"
              @click="deletePipeline(pipeline)"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <!-- Draggable regular stages -->
          <div
            v-for="stage in draggableStages(pipeline)"
            :key="stage.id"
            class="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg transition-opacity"
            :class="draggingStageId === stage.id ? 'opacity-40' : ''"
            :style="
              editingStage !== stage.id
                ? {
                    backgroundColor: (stage.color || '#6366f1') + '22',
                    borderLeft: `3px solid ${stage.color || '#6366f1'}`,
                    outline:
                      dragOverStageId === stage.id
                        ? `2px solid ${stage.color || '#6366f1'}`
                        : 'none',
                  }
                : {}
            "
            :draggable="editingStage !== stage.id"
            @dragstart.stop="onStageDragStart($event, stage)"
            @dragover.stop="onStageDragOver($event, stage)"
            @drop.stop="onStageDrop($event, pipeline, stage)"
            @dragend.stop="onStageDragEnd"
          >
            <div
              v-if="editingStage === stage.id"
              class="flex flex-col gap-2 flex-1"
            >
              <div class="flex gap-2">
                <Input
                  v-model="editingStageName"
                  custom-input-class="h-7"
                  class="flex-1"
                  autofocus
                  @keydown.enter="saveEditStage(pipeline, stage)"
                  @keydown.escape="editingStage = null"
                />
                <Button
                  :label="t('PIPELINE_SETTINGS.SAVE')"
                  color="blue"
                  size="xs"
                  @click="saveEditStage(pipeline, stage)"
                />
                <Button
                  :label="t('PIPELINE_SETTINGS.CANCEL')"
                  variant="ghost"
                  size="xs"
                  @click="editingStage = null"
                />
              </div>
              <div class="flex items-center gap-2 flex-wrap">
                <button
                  v-for="color in PRESET_COLORS"
                  :key="color"
                  type="button"
                  class="w-5 h-5 rounded-full border-2 transition-transform hover:scale-110"
                  :style="{
                    backgroundColor: color,
                    borderColor:
                      editingStageColor === color ? color : 'transparent',
                  }"
                  :class="
                    editingStageColor === color
                      ? 'ring-2 ring-offset-1 ring-n-slate-8'
                      : ''
                  "
                  @click="editingStageColor = color"
                />
                <input
                  v-model="editingStageColor"
                  type="color"
                  class="w-5 h-5 rounded cursor-pointer border border-n-weak"
                  :title="t('PIPELINE_SETTINGS.CUSTOM_COLOR')"
                />
              </div>
            </div>
            <div v-else class="flex items-center gap-2 flex-1 min-w-0">
              <span
                class="i-lucide-grip-vertical text-n-slate-7 cursor-grab active:cursor-grabbing flex-shrink-0"
              />
              <span class="text-sm font-medium text-n-slate-11 truncate">{{
                stage.name
              }}</span>
            </div>
            <div
              v-if="editingStage !== stage.id"
              class="flex gap-1 flex-shrink-0"
            >
              <Button
                icon="i-lucide-pencil"
                variant="ghost"
                size="xs"
                color="slate"
                @click="startEditStage(stage)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                size="xs"
                color="ruby"
                @click="deleteStage(pipeline, stage)"
              />
            </div>
          </div>

          <!-- Terminal stages (won/lost) — locked at bottom -->
          <div
            v-for="stage in terminalStages(pipeline)"
            :key="stage.id"
            class="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg"
            :style="
              editingStage !== stage.id
                ? {
                    backgroundColor: (stage.color || '#6366f1') + '22',
                    borderLeft: `3px solid ${stage.color || '#6366f1'}`,
                  }
                : {}
            "
          >
            <div
              v-if="editingStage === stage.id"
              class="flex flex-col gap-2 flex-1"
            >
              <div class="flex gap-2">
                <Input
                  v-model="editingStageName"
                  custom-input-class="h-7"
                  class="flex-1"
                  autofocus
                  @keydown.enter="saveEditStage(pipeline, stage)"
                  @keydown.escape="editingStage = null"
                />
                <Button
                  :label="t('PIPELINE_SETTINGS.SAVE')"
                  color="blue"
                  size="xs"
                  @click="saveEditStage(pipeline, stage)"
                />
                <Button
                  :label="t('PIPELINE_SETTINGS.CANCEL')"
                  variant="ghost"
                  size="xs"
                  @click="editingStage = null"
                />
              </div>
              <div class="flex items-center gap-2 flex-wrap">
                <button
                  v-for="color in PRESET_COLORS"
                  :key="color"
                  type="button"
                  class="w-5 h-5 rounded-full border-2 transition-transform hover:scale-110"
                  :style="{
                    backgroundColor: color,
                    borderColor:
                      editingStageColor === color ? color : 'transparent',
                  }"
                  :class="
                    editingStageColor === color
                      ? 'ring-2 ring-offset-1 ring-n-slate-8'
                      : ''
                  "
                  @click="editingStageColor = color"
                />
                <input
                  v-model="editingStageColor"
                  type="color"
                  class="w-5 h-5 rounded cursor-pointer border border-n-weak"
                  :title="t('PIPELINE_SETTINGS.CUSTOM_COLOR')"
                />
              </div>
            </div>
            <div v-else class="flex items-center gap-2 flex-1 min-w-0">
              <span
                class="i-lucide-lock text-n-slate-7 flex-shrink-0 text-xs"
              />
              <span class="text-sm font-medium text-n-slate-11 truncate">{{
                stage.name
              }}</span>
            </div>
            <div
              v-if="editingStage !== stage.id"
              class="flex gap-1 flex-shrink-0"
            >
              <Button
                icon="i-lucide-pencil"
                variant="ghost"
                size="xs"
                color="slate"
                @click="startEditStage(stage)"
              />
            </div>
          </div>

          <div class="flex gap-2 mt-1">
            <Input
              v-model="newStageName[pipeline.id]"
              :placeholder="t('PIPELINE_SETTINGS.NEW_STAGE_PLACEHOLDER')"
              custom-input-class="h-7 text-xs"
              class="flex-1"
              @keydown.enter="createStage(pipeline)"
            />
            <Button
              icon="i-lucide-plus"
              variant="ghost"
              size="xs"
              color="slate"
              :disabled="
                savingStage || !(newStageName[pipeline.id] || '').trim()
              "
              @click="createStage(pipeline)"
            />
          </div>
        </div>
      </div>
    </div>

    <ConfirmPipelineDeleteDialog
      ref="deleteDialogRef"
      :pipeline="pipelineToDelete"
      :pipelines="pipelines"
      @move="handleMoveDeals"
      @delete-deals="handleDeleteDeals"
    />
  </SettingsLayout>
</template>
